import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import generateUniqueId from '@insight/toolkit-utils/lib/generators/generateUniqueId';

import { generateComponentForField, FIELD_COMPONENTS } from './componentMap';
import {
  CharacterCounter,
  FieldError,
  HelpText,
  Label,
  Legend,
} from './Decorators';

const ALLOWED_COUNTER_INPUTS = ['Password', 'Search', 'Text', 'TextArea'];

const ComponentDecorator = ({ fieldComponent, fieldProps }) => {
  const {
    className,
    errors,
    helpText,
    horizontal,
    id = generateUniqueId(),
    label,
    legend,
    max,
    name,
    required,
    showCounter,
    showErrorIcon,
    infoText,
    ...rest
  } = fieldProps;

  const hasErrors = errors && errors.length > 0;
  const shouldHaveLegend =
    fieldComponent === 'Radio' ||
    fieldComponent === 'RadioGroup' ||
    fieldComponent === 'CheckboxGroup';
  const isPasswordField = fieldComponent === 'Password';

  const optionalCounter =
    showCounter &&
    (() => {
      if (!max) {
        throw new Error(
          `You need to pass a max prop to <Field> when showCounter is true`
        );
      }

      if (!ALLOWED_COUNTER_INPUTS.includes(fieldComponent)) {
        if (typeof fieldComponent !== 'function') {
          throw new Error(
            `You can only show a character counter for these fields: ${ALLOWED_COUNTER_INPUTS.join('\n')}`
          );
        } else if (process.env.NODE_ENV === 'development') {
          console.warn(
            `You are using a counter component with an un-typed input component. Be careful, this might not make sense`
          );
        }
      }

      return <CharacterCounter max={max} value={rest.value} />
    })();

  const optionalHelpText = helpText && (
    <HelpText id={`${id}-help`}>{helpText}</HelpText>
  );
  const optionalInfoText = infoText && (
    <HelpText id={`${id}-info`} className="c-form__info">
      {infoText}
    </HelpText>
  );
  const optionalLabel = label && !shouldHaveLegend && (
    <Label aria-invalid={hasErrors} id={id} required={required}>
      {label}
    </Label>
  );

  const optionalLegend = legend && shouldHaveLegend && (
    <Legend required={required}>{legend}</Legend>
  );
  const optionalErrors =
    hasErrors &&
    errors.map((error, idx) => (
      <FieldError
        id={`${id}-error-${idx}`}
        key={`${id}-${idx}`}
        showErrorIcon={showErrorIcon}
      >
        {error}
      </FieldError>
    ));

  const labelElement = optionalLabel || optionalLegend;

  const classes = cn(
    'c-form__element',
    {
      'c-form__element--relative': isPasswordField,
      'has-error': hasErrors,
      'is-disabled': fieldProps.disabled,
    },
    className
  );

  const ariaProps = generateAriaProps(optionalHelpText, optionalErrors);
  const decoratorProps = {
    horizontal,
    id,
    label,
    max,
    name,
    required,
    ...rest,
    ...ariaProps,
  };

  const CustomTag = shouldHaveLegend ? 'fieldset' : 'div';

  return (
    <CustomTag className={classes}>
      {labelElement}
      <div className="c-form__control">
        {optionalInfoText}
        {horizontal ? (
          <div className="o-grid o-grid--gutters">
            {generateComponentForField(fieldComponent, decoratorProps)}
          </div>
        ) : (
          generateComponentForField(fieldComponent, decoratorProps)
        )}
        {optionalErrors}
        {optionalHelpText}
      </div>
      {optionalCounter}
    </CustomTag>
  );
};

const generateAriaProps = (helptext, errortext) => {
  const describingIds = [];
  const optionalProps = {};
  if (helptext) {
    describingIds.push(helptext.props.id);
  }
  if (errortext) {
    errortext.forEach((error) => describingIds.push(error.props.id));
  }
  if (describingIds.length > 0) {
    optionalProps['aria-describedby'] = describingIds.join(' ');
  }
  return optionalProps;
};

export default ComponentDecorator;

ComponentDecorator.propTypes = {
  fieldComponent: PropTypes.oneOf(FIELD_COMPONENTS),
  fieldProps: PropTypes.shape({
    className: PropTypes.string,
    errors: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.object, PropTypes.string])
    ),
    helpText: PropTypes.node,
    horizontal: PropTypes.bool,
    id: PropTypes.string,
    label: PropTypes.node,
    legend: PropTypes.node,
    max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string.isRequired,
    required: PropTypes.bool,
    showCounter: false,
    showErrorIcon: PropTypes.bool,
  }).isRequired,
};
